/**
 * Households API Service
 *
 * API client for household management endpoints (WP-HOUSEHOLD-3).
 * Provides functions for managing household members and invitations.
 *
 * SECURITY:
 * - No PII in console logs
 * - All requests use httpOnly cookies for auth
 */

import { api } from './api';
import type {
  HouseholdListResponse,
  MemberListResponse,
  InvitationListResponse,
  InvitationResponse,
  CodeInvitationResponse,
  AcceptInvitationResponse,
  InvitationPreview,
  LeaveResponse,
  MessageResponse,
  SetDefaultHouseholdResponse,
  UpdateHouseholdResponse,
} from '@/types/household';

/**
 * Households API methods
 */
export const householdsApi = {
  /**
   * Get all households the current user belongs to
   */
  getMyHouseholds: () => api.get<HouseholdListResponse>('/households/me/households'),

  /**
   * Get members of a household
   * @param tenantId - The household/tenant ID
   */
  getMembers: (tenantId: string) =>
    api.get<MemberListResponse>(`/households/${tenantId}/members`),

  /**
   * Create an email invitation to join a household
   * @param tenantId - The household/tenant ID
   * @param email - Email address to invite
   */
  createEmailInvitation: (tenantId: string, email: string) =>
    api.post<InvitationResponse>(`/households/${tenantId}/invitations/email`, { email }),

  /**
   * Create a shareable code invitation
   * @param tenantId - The household/tenant ID
   */
  createCodeInvitation: (tenantId: string) =>
    api.post<CodeInvitationResponse>(`/households/${tenantId}/invitations/code`),

  /**
   * List pending invitations for a household
   * @param tenantId - The household/tenant ID
   */
  listInvitations: (tenantId: string) =>
    api.get<InvitationListResponse>(`/households/${tenantId}/invitations`),

  /**
   * Revoke a pending invitation
   * @param tenantId - The household/tenant ID
   * @param invitationId - The invitation ID to revoke
   */
  revokeInvitation: (tenantId: string, invitationId: string) =>
    api.delete<MessageResponse>(`/households/${tenantId}/invitations/${invitationId}`),

  /**
   * Accept an invitation using a token or code
   * @param tokenOrCode - The invitation token (from email) or 6-char code
   */
  acceptInvitation: (tokenOrCode: string) =>
    api.post<AcceptInvitationResponse>('/households/invitations/accept', {
      token_or_code: tokenOrCode,
    }),

  /**
   * Get invitation preview (unauthenticated)
   * @param token - The invitation token or code
   */
  getInvitationPreview: (token: string) =>
    api.get<InvitationPreview>(`/households/invitations/${token}`),

  /**
   * Remove a member from a household (owner only)
   * @param tenantId - The household/tenant ID
   * @param userId - The user ID to remove
   */
  removeMember: (tenantId: string, userId: string) =>
    api.delete<MessageResponse>(`/households/${tenantId}/members/${userId}`),

  /**
   * Leave a household voluntarily
   * @param tenantId - The household/tenant ID
   */
  leaveHousehold: (tenantId: string) =>
    api.post<LeaveResponse>(`/households/${tenantId}/leave`),

  /**
   * Transfer ownership to another member (owner only)
   * @param tenantId - The household/tenant ID
   * @param newOwnerId - The user ID of the new owner
   */
  transferOwnership: (tenantId: string, newOwnerId: string) =>
    api.post<MessageResponse>(`/households/${tenantId}/transfer-ownership`, {
      new_owner_id: newOwnerId,
    }),

  /**
   * Set the user's default household
   * WP-HOUSEHOLD-2: Default household is used on login
   * @param tenantId - The household/tenant ID to set as default
   */
  setDefaultHousehold: (tenantId: string) =>
    api.post<SetDefaultHouseholdResponse>('/households/default', {
      tenant_id: tenantId,
    }),

  /**
   * Update the current household's settings (owner only)
   * WP-HOUSEHOLD-2: Allows renaming the household
   * @param name - New household name
   */
  updateHousehold: (name: string) =>
    api.patch<UpdateHouseholdResponse>('/households/current', { name }),
};

export default householdsApi;
